import { useState, useEffect, useRef } from "react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  MessageSquare,
  Send,
  PlusCircle,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Ticket,
  Lightbulb,
  Flag,
  Bug,
  HelpCircle,
} from "lucide-react";

const FEEDBACK_TYPES = [
  { value: "suggestion", label: "Suggestion", icon: Lightbulb, color: "text-yellow-600 bg-yellow-50" },
  { value: "complaint", label: "Complaint", icon: Flag, color: "text-red-600 bg-red-50" },
  { value: "bug", label: "Bug Report", icon: Bug, color: "text-purple-600 bg-purple-50" },
  { value: "other", label: "Other", icon: HelpCircle, color: "text-blue-600 bg-blue-50" },
];

export default function Support() {
  const { user } = useAuth();
  // Default to "tickets" (Customer Care) instead of "feedback"
  const [activeTab, setActiveTab] = useState("tickets");

  // Feedback state
  const [feedbackType, setFeedbackType] = useState("suggestion");
  const [feedbackMsg, setFeedbackMsg] = useState("");
  const [sendingFeedback, setSendingFeedback] = useState(false);

  // Tickets state
  const [tickets, setTickets] = useState([]);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [creatingTicket, setCreatingTicket] = useState(false);
  const [sendingMsg, setSendingMsg] = useState(false);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (activeTab === "tickets") loadTickets();
  }, [activeTab]);

  useEffect(() => {
    if (selectedTicket) {
      loadMessages(selectedTicket.ticket_id);
      pollRef.current = setInterval(() => loadMessages(selectedTicket.ticket_id), 2000);
    }
    return () => clearInterval(pollRef.current);
  }, [selectedTicket]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // --- Feedback ---
  const handleSubmitFeedback = async () => {
    if (!feedbackMsg.trim()) {
      toast.error("Please enter your feedback");
      return;
    }
    setSendingFeedback(true);
    try {
      await api.post("/feedback", {
        type: feedbackType,
        message: feedbackMsg.trim(),
      });
      toast.success("Feedback sent! Thank you.");
      setFeedbackMsg("");
      setFeedbackType("suggestion");
    } catch (err) {
      toast.error("Failed to send feedback");
    } finally {
      setSendingFeedback(false);
    }
  };

  // --- Tickets ---
  const loadTickets = async () => {
    try {
      const res = await api.get("/support/tickets");
      setTickets(res.data || []);
    } catch (err) {
      toast.error("Failed to load tickets");
    }
  };

  const loadMessages = async (ticketId) => {
    try {
      const res = await api.get(`/support/tickets/${ticketId}/messages`);
      setMessages(res.data || []);
    } catch (err) {}
  };

  const createTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setCreatingTicket(true);
    try {
      const res = await api.post("/support/tickets", {
        subject: ticketSubject.trim(),
        message: ticketMessage.trim(),
      });
      toast.success("Ticket created! We'll get back to you soon.");
      setShowNewTicketForm(false);
      setTicketSubject("");
      setTicketMessage("");
      await loadTickets();
      // Auto-select the new ticket
      const newTicket = { ticket_id: res.data.ticket_id, subject: ticketSubject.trim(), status: "open", ...res.data };
      setSelectedTicket(newTicket);
    } catch (err) {
      toast.error("Failed to create ticket");
    } finally {
      setCreatingTicket(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedTicket) return;
    setSendingMsg(true);
    try {
      await api.post(`/support/tickets/${selectedTicket.ticket_id}/messages`, {
        content: newMessage.trim(),
      });
      setNewMessage("");
      await loadMessages(selectedTicket.ticket_id);
    } catch (err) {
      toast.error("Failed to send message");
    } finally {
      setSendingMsg(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const getStatusBadge = (status) => {
    if (status === "open") return <span className="text-green-600 text-xs flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-500" /> Open</span>;
    if (status === "closed") return <span className="text-gray-400 text-xs flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-gray-400" /> Closed</span>;
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-semibold">Help & Support</h1>
            <p className="text-sm text-[#6B6B70]">
              Send feedback or chat with our team
            </p>
          </div>
        </div>

        {/* Tabs – Customer Care first, Feedback second */}
        <div className="flex gap-2 mb-6 border-b border-[#E7E5E0] pb-3">
          <button
            onClick={() => setActiveTab("tickets")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "tickets"
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Ticket className="w-4 h-4" /> Customer Care
          </button>
          <button
            onClick={() => setActiveTab("feedback")}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === "feedback"
                ? "bg-indigo-50 text-indigo-700"
                : "text-gray-600 hover:text-gray-900"
            }`}
          >
            <Lightbulb className="w-4 h-4" /> Feedback
          </button>
        </div>

        {/* Feedback Tab */}
        {activeTab === "feedback" && (
          <div className="max-w-lg mx-auto">
            <div className="bg-white border border-[#E7E5E0] rounded-2xl p-6 space-y-4">
              <h3 className="font-semibold text-lg">Send Feedback</h3>
              <p className="text-sm text-gray-500">
                We value your input! Let us know how we can improve Haven.
              </p>

              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Type</label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {FEEDBACK_TYPES.map((item) => (
                    <button
                      key={item.value}
                      onClick={() => setFeedbackType(item.value)}
                      className={`flex items-center gap-2 p-3 rounded-xl border text-sm font-medium transition-colors ${
                        feedbackType === item.value
                          ? `${item.color} border-${item.color.split(" ")[1]} ring-2 ring-offset-1 ring-indigo-500`
                          : "bg-gray-50 border-gray-200 hover:bg-gray-100"
                      }`}
                    >
                      <item.icon className="w-5 h-5" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold uppercase text-gray-500">Message</label>
                <textarea
                  value={feedbackMsg}
                  onChange={(e) => setFeedbackMsg(e.target.value)}
                  placeholder="Describe your suggestion or issue..."
                  className="neo-input mt-2 h-28 resize-none"
                  maxLength={1000}
                />
              </div>

              <button
                onClick={handleSubmitFeedback}
                disabled={!feedbackMsg.trim() || sendingFeedback}
                className="w-full neo-btn bg-indigo-500 border-indigo-500 flex items-center justify-center gap-2"
              >
                {sendingFeedback ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {sendingFeedback ? "Sending..." : "Submit Feedback"}
              </button>
            </div>
          </div>
        )}

        {/* Tickets Tab (Customer Care) */}
        {activeTab === "tickets" && (
          <div className="flex gap-6">
            {/* Tickets List Sidebar */}
            <div className="w-1/3 bg-white border rounded-2xl p-4 space-y-2">
              <button
                onClick={() => {
                  setShowNewTicketForm(true);
                  setSelectedTicket(null);
                }}
                className="w-full flex items-center gap-2 bg-indigo-500 text-white p-2 rounded-xl text-sm font-medium hover:bg-indigo-600 transition-colors mb-4"
              >
                <PlusCircle className="w-4 h-4" /> New Ticket
              </button>

              {tickets.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No tickets yet</p>
              ) : (
                tickets.map((ticket) => (
                  <button
                    key={ticket.ticket_id}
                    onClick={() => {
                      setSelectedTicket(ticket);
                      setShowNewTicketForm(false);
                    }}
                    className={`w-full text-left p-3 rounded-lg text-sm transition-colors ${
                      selectedTicket?.ticket_id === ticket.ticket_id
                        ? "bg-indigo-50 border border-indigo-200"
                        : "hover:bg-gray-50 border border-transparent"
                    }`}
                  >
                    <div className="font-medium truncate">{ticket.subject}</div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                      {ticket.status === "open" ? (
                        <Clock className="w-3 h-3 text-green-500" />
                      ) : (
                        <CheckCircle className="w-3 h-3 text-gray-400" />
                      )}
                      {ticket.status}
                      <span className="ml-auto">
                        {ticket.updated_at && formatTime(ticket.updated_at)}
                      </span>
                    </div>
                  </button>
                ))
              )}
            </div>

            {/* Chat / New Ticket Form */}
            <div className="flex-1 bg-white border rounded-2xl p-4 flex flex-col min-h-[500px]">
              {showNewTicketForm ? (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">New Support Ticket</h3>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Subject</label>
                    <input
                      type="text"
                      value={ticketSubject}
                      onChange={(e) => setTicketSubject(e.target.value)}
                      placeholder="Brief summary of your issue"
                      className="neo-input mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase text-gray-500">Message</label>
                    <textarea
                      value={ticketMessage}
                      onChange={(e) => setTicketMessage(e.target.value)}
                      placeholder="Describe your problem in detail..."
                      className="neo-input mt-1 h-32 resize-none"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={createTicket}
                      disabled={creatingTicket}
                      className="neo-btn bg-indigo-500 border-indigo-500 flex items-center gap-2"
                    >
                      {creatingTicket ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                      {creatingTicket ? "Creating..." : "Create Ticket"}
                    </button>
                    <button
                      onClick={() => setShowNewTicketForm(false)}
                      className="neo-btn neo-btn-secondary"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : selectedTicket ? (
                <>
                  {/* Ticket Header */}
                  <div className="flex items-center justify-between border-b pb-3 mb-3">
                    <div>
                      <h3 className="font-semibold text-lg truncate">{selectedTicket.subject}</h3>
                      <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                        {getStatusBadge(selectedTicket.status)}
                        <span className="text-gray-300">|</span>
                        <span>Created {new Date(selectedTicket.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto space-y-3 mb-4 pr-2">
                    {messages.map((msg) => {
                      const isOwn = msg.sender_id === user?.user_id;
                      return (
                        <div key={msg.message_id} className={`flex ${isOwn ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${
                              isOwn
                                ? "bg-indigo-500 text-white rounded-br-md"
                                : "bg-gray-100 text-gray-900 rounded-bl-md"
                            }`}
                          >
                            <p>{msg.content}</p>
                            <div className="text-[10px] opacity-70 mt-1 text-right">
                              {formatTime(msg.created_at)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input (only if ticket is open) */}
                  {selectedTicket.status === "open" && (
                    <div className="flex gap-2 mt-2">
                      <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                        placeholder="Type your reply..."
                        className="flex-1 px-4 py-2.5 bg-gray-100 rounded-xl text-sm outline-none"
                        disabled={sendingMsg}
                      />
                      <button
                        onClick={sendMessage}
                        disabled={!newMessage.trim() || sendingMsg}
                        className="bg-indigo-500 text-white p-2.5 rounded-xl hover:bg-indigo-600 disabled:opacity-50 transition-colors"
                      >
                        {sendingMsg ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400 text-sm flex-col gap-2">
                  <MessageSquare className="w-12 h-12 opacity-20" />
                  Select a ticket from the list or create a new one
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}